import { MenuItem, Select, styled } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { getMonth, getYear } from 'date-fns'
import { colorPalette } from '../../../style/colorPalette'

type DatePickerCustomHeaderProps = {
  date: Date
  changeYear: (year: number) => void
  changeMonth: (month: number) => void
  decreaseMonth: () => void
  increaseMonth: () => void
  prevMonthButtonDisabled: boolean
  nextMonthButtonDisabled: boolean
}

const YEAR_RANGE_START = 2000
const YEAR_RANGE_END = 2100
const YEARS = Array.from(
  { length: YEAR_RANGE_END - YEAR_RANGE_START + 1 },
  (_, i) => YEAR_RANGE_START + i
)
// カレンダー本体（曜日ヘッダー等）は react-datepicker 既定の英語表記のため、月名も英語で揃える
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// react-datepicker は react-onclickoutside の DOM 判定でカレンダーを閉じるため、
// document.body へ Portal されるメニューは外側クリックとみなされる。
// react-datepicker が用意している除外クラスを付けて閉じないようにする
const DATEPICKER_IGNORE_CLICK_OUTSIDE_CLASS =
  'react-datepicker-ignore-onclickoutside'

const MENU_PROPS = {
  className: DATEPICKER_IGNORE_CLICK_OUTSIDE_CLASS,
  sx: {
    zIndex: 2100,
  },
  PaperProps: {
    sx: {
      maxHeight: 200,
    },
  },
}

export const DatePickerCustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: DatePickerCustomHeaderProps) => (
  <HeaderContainer>
    <NavButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
      ‹
    </NavButton>
    <SelectGroup>
      <HeaderSelect
        value={getMonth(date)}
        onChange={e => changeMonth(Number(e.target.value))}
        variant="standard"
        disableUnderline={true}
        MenuProps={MENU_PROPS}
      >
        {MONTHS.map((m, i) => (
          <MenuItem key={m} value={i}>
            {m}
          </MenuItem>
        ))}
      </HeaderSelect>
      <HeaderSelect
        value={getYear(date)}
        onChange={e => changeYear(Number(e.target.value))}
        variant="standard"
        disableUnderline={true}
        MenuProps={MENU_PROPS}
      >
        {YEARS.map(y => (
          <MenuItem key={y} value={y}>
            {y}
          </MenuItem>
        ))}
      </HeaderSelect>
    </SelectGroup>
    <NavButton onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
      ›
    </NavButton>
  </HeaderContainer>
)

const HeaderContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 8px',
  backgroundColor: colorPalette.monotone[1],
})

const HeaderSelect = styled(Select)({
  fontSize: '0.944rem !important',
  fontWeight: '400 !important',
  color: `${colorPalette.monotone[5]} !important`,
  '& .MuiSelect-select': {
    padding: '2px 24px 2px 4px !important',
    fontSize: '0.944rem !important',
    fontWeight: '400 !important',
    color: `${colorPalette.monotone[5]} !important`,
  },
  '& .MuiSelect-icon': {
    color: colorPalette.monotone[5],
    right: 2,
  },
  '&:hover': {
    backgroundColor: alpha(colorPalette.monotone[5], 0.08),
    borderRadius: 1,
  },
})

const SelectGroup = styled('div')({
  display: 'flex',
  gap: 2,
  alignItems: 'center',
})

const NavButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: 'none',
  borderRadius: 4,
  backgroundColor: 'transparent',
  color: colorPalette.monotone[5],
  fontSize: 18,
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'default',
  },
})
